import React, { useState } from 'react';
import LinkIcon from '@material-ui/icons/Link';
import MuiOutlinedInput from '@material-ui/core/OutlinedInput';
import Tooltip from '@material-ui/core/Tooltip';
import {
  EditorState,
  CompositeDecorator,
  Modifier,
  RichUtils,
  SelectionState
} from 'draft-js';
import CheckCircleOutlineRoundedIcon from '@material-ui/icons/CheckCircleOutlineRounded';
import HighlightOffRoundedIcon from '@material-ui/icons/HighlightOffRounded';
import IconButton from '@material-ui/core/IconButton';

import styled from 'styled-components/macro';

const OutlinedInput = styled(MuiOutlinedInput)`
  && {
    margin: 7.5px 0;
    background: #fff;
  }
  input {
    padding: 0.1em 0.8em;
    margin: 0.4em 0;
  }
`;

const Label = styled.label`
  margin: 0 0.8em;
`;

const InputsWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const ButtonsWrapper = styled.span`
  margin: 0 0.8em;
`;

const linkStrategy = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges(character => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === 'LINK'
    );
  }, callback);
};

const Link = props => {
  const { contentState, entityKey } = props;
  const { url } = contentState.getEntity(entityKey).getData();
  return (
    <a
      className="link"
      href={url}
      rel="noopener noreferrer"
      target="_blank"
      aria-label={url}
      title={url}
    >
      {props.children}
    </a>
  );
};

export const decorator = new CompositeDecorator([
  {
    strategy: linkStrategy,
    component: Link
  }
]);

const LinkButton = props => {
  return (
    <div className="linkButtonWrapper">
      <button
        className="linkButton"
        id="link_url"
        onMouseDown={e => {
          e.preventDefault();
        }}
        onClick={() => showLinkToolbar(props)}
        {...props}
      >
        <LinkIcon />
      </button>
    </div>
  );
};

const showLinkToolbar = props => {
  const { editorState, setEditorState } = props;
  const cancelFunc = () => {
    props.onOverrideContent(null);
  };

  const findStartAndEnd = (block, id) => {
    let i, start, end, length, foundStart, foundEnd;
    length = block.getLength();
    for (i = 0; i < length && !foundEnd; i++) {
      if (!foundStart && block.getEntityAt(i) === id) {
        foundStart = true;
        start = i;
      }
      if (foundStart && block.getEntityAt(i) !== id) {
        foundEnd = true;
        end = i;
      }
    }
    if (foundEnd) {
      return { start: start, end: end };
    }
    return null;
  };

  const LinkPicker = () => {
    let selectionState = editorState.getSelection();
    let anchorKey = selectionState.getAnchorKey();
    let currentContent = editorState.getCurrentContent();
    let currentContentBlock = currentContent.getBlockForKey(anchorKey);
    let start = selectionState.getStartOffset();
    let end = selectionState.getEndOffset();
    let selectedText = currentContentBlock.getText().slice(start, end);
    let foundStartAndEnd = {};
    let possiblyModifiedEditorState = editorState;

    let existingLink, existingEntityId, existingEntityBlock, entityData;
    existingLink = '';
    try {
      existingEntityId = currentContentBlock.getEntityAt(start);
      existingEntityBlock = currentContent.getEntity(existingEntityId);
      entityData = existingEntityBlock.getData();
      if (entityData && entityData.url) {
        existingLink = entityData.url;
      }
      foundStartAndEnd = findStartAndEnd(
        currentContent.getBlockForKey(anchorKey),
        existingEntityId
      );
      if (foundStartAndEnd) {
        start = foundStartAndEnd.start;
        end = foundStartAndEnd.end;
        selectedText = currentContentBlock.getText().slice(start, end);
        const newSelection = new SelectionState({
          anchorKey: anchorKey,
          anchorOffset: start,
          focusKey: anchorKey,
          focusOffset: end
        });
        // now lets add cursor right after the inserted link
        possiblyModifiedEditorState = EditorState.forceSelection(
          editorState,
          newSelection
        );
      }
    } catch (e) {}

    const [text, setText] = useState(selectedText);
    const [link, setLink] = useState(existingLink);
    return (
      <InputsWrapper
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <Label>Text</Label>
        <OutlinedInput
          value={text || ''}
          onChange={e => {
            const v = e.target.value;
            setText(v);
          }}
        />
        <Label>Link</Label>
        <OutlinedInput
          value={link || ''}
          onChange={e => {
            const v = e.target.value;
            setLink(v);
          }}
        />
        <ButtonsWrapper>
          <Tooltip arrow title="Confirm">
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                onAddLink(
                  text,
                  link,
                  cancelFunc,
                  possiblyModifiedEditorState,
                  setEditorState
                )
              }
              onMouseDown={e => {
                e.preventDefault();
              }}
            >
              {/* Confirm*/}
              <CheckCircleOutlineRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip arrow title="Cancel">
            <IconButton
              size="small"
              onClick={cancelFunc}
              onMouseDown={e => {
                e.preventDefault();
              }}
            >
              {/* Cancel*/}
              <HighlightOffRoundedIcon />
            </IconButton>
          </Tooltip>
        </ButtonsWrapper>
      </InputsWrapper>
    );
  };

  props.onOverrideContent(LinkPicker);
};

const onAddLink = (text, link, callback, editorState, setEditorState) => {
  let urlWithProtocol;
  const contentState = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  if (!link) {
    setEditorState(RichUtils.toggleLink(editorState, selection, null));
    if (callback) {
      callback();
    }
    return 'handled';
  }

  urlWithProtocol = link;

  if (link.indexOf('://') === -1) {
    urlWithProtocol = 'https://' + link;
  }

  const anchorKey = selection.getAnchorKey();
  const endOffset = selection.getEndOffset();

  const zeroLengthSelection = new SelectionState({
    anchorKey: anchorKey,
    anchorOffset: endOffset,
    focusKey: anchorKey,
    focusOffset: endOffset
  });

  // const textWithSpace = text.concat(' ');
  // create new content with text
  const newContent = Modifier.insertText(
    contentState,
    zeroLengthSelection,
    text
  );
  // create new link entity
  const newContentWithEntity = newContent.createEntity(
    'LINK',
    'MUTABLE',
    { url: urlWithProtocol },
    false
  );
  const entityKey = newContentWithEntity.getLastCreatedEntityKey();
  // create new selection with the inserted text
  const newSelection = new SelectionState({
    anchorKey: anchorKey,
    anchorOffset: endOffset,
    focusKey: anchorKey,
    focusOffset: endOffset + text.length
  });
  // and aply link entity to the inserted text
  const newContentWithLink = Modifier.applyEntity(
    newContentWithEntity,
    newSelection,
    entityKey
  );
  // create new state with link text
  const withLinkText = EditorState.push(
    editorState,
    newContentWithLink,
    'insert-characters'
  );

  const newContentWithoutOriginal = Modifier.removeRange(
    withLinkText.getCurrentContent(),
    selection,
    'forward'
  );

  const withoutOriginal = EditorState.push(
    editorState,
    newContentWithoutOriginal,
    'remove-range'
  );

  // now lets add cursor right after the inserted link
  const withProperCursor = EditorState.forceSelection(
    withoutOriginal,
    newContent.getSelectionAfter()
  );

  // update the editor with all changes
  setEditorState(withProperCursor);
  if (callback) {
    callback();
  }
  return 'handled';
};

export default LinkButton;
